import React, { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { Context } from './context';
import * as Types from '../types';
import config from 'config';

const ACTIVE_ORG_KEY = config.api.active_org_key;

// Check localStorage synchronously to get initial auth state
// This runs once at module load time to avoid any timing issues
function getInitialAuthState(): Types.IContext.State {
  const accessToken = localStorage.getItem(config.api.access_token_key);
  const refreshToken = localStorage.getItem(config.api.refresh_token_key);
  const hasTokens = Boolean(accessToken && refreshToken);

  return {
    user: null,
    organization: null,
    organizations: [],
    role: null,
    permissions: [],
    entitlements: null,
    isAuthenticated: hasTokens,
    isInitialized: true, // Always initialized since we check synchronously
    isFetched: false,
    accessFetched: false,
  };
}

// Ask the server to end the session (POST /auth/logout). Uses bare axios with the token
// captured before local cleanup (the shared client reads localStorage, which is already
// cleared) and is best-effort: the user is signed out locally whether or not it succeeds.
function serverLogout(accessToken: string | null) {
  if (!accessToken) return;
  axios
    .post(`${config.api.base_url}/api/auth/logout`, undefined, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    .catch(() => undefined);
}

interface IProps {
  children: React.ReactNode;
}

export const Provider: React.FC<IProps> = ({ children }) => {
  // Initialize with tokens from localStorage (synchronous check)
  const [state, setState] = useState<Types.IContext.State>(getInitialAuthState);
  const queryClient = useQueryClient();

  const setUser = useCallback((user: React.SetStateAction<Types.IEntity.User | null>) => {
    setState((prev) => ({ ...prev, user: typeof user === 'function' ? user(prev.user) : user }));
  }, []);

  const setOrganization = useCallback((organization: Types.IEntity.Organization | null) => {
    setState((prev) => ({ ...prev, organization }));
  }, []);

  const setOrganizations = useCallback((organizations: Types.IEntity.OrganizationWithRole[]) => {
    setState((prev) => ({ ...prev, organizations }));
  }, []);

  const setRole = useCallback((role: string | null) => {
    setState((prev) => ({ ...prev, role }));
  }, []);

  const setPermissions = useCallback((permissions: Types.IContext.State['permissions']) => {
    setState((prev) => ({ ...prev, permissions }));
  }, []);

  const setEntitlements = useCallback((entitlements: Types.IEntity.OrgEntitlements | null) => {
    setState((prev) => ({ ...prev, entitlements }));
  }, []);

  const setIsAuthenticated = useCallback((isAuthenticated: boolean) => {
    setState((prev) => ({ ...prev, isAuthenticated }));
  }, []);

  const setIsInitialized = useCallback((isInitialized: boolean) => {
    setState((prev) => ({ ...prev, isInitialized }));
  }, []);

  const setIsFetched = useCallback((isFetched: boolean) => {
    setState((prev) => ({ ...prev, isFetched }));
  }, []);

  const setAccessFetched = useCallback((accessFetched: boolean) => {
    setState((prev) => ({ ...prev, accessFetched }));
  }, []);

  const setTokens = useCallback((tokens: { accessToken: string; refreshToken: string }) => {
    localStorage.setItem(config.api.access_token_key, tokens.accessToken);
    localStorage.setItem(config.api.refresh_token_key, tokens.refreshToken);
    queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
    // Treat a new token pair as a new session boundary. Keeping the previous
    // profile/access in memory lets guards and nav render from stale data before
    // useProfile reloads the active org, permissions, and entitlements.
    setState({
      user: null,
      organization: null,
      organizations: [],
      role: null,
      permissions: [],
      entitlements: null,
      isAuthenticated: true,
      isInitialized: true,
      isFetched: false,
      accessFetched: false,
    });
  }, [queryClient]);

  const logout = useCallback(() => {
    serverLogout(localStorage.getItem(config.api.access_token_key));
    localStorage.removeItem(config.api.access_token_key);
    localStorage.removeItem(config.api.refresh_token_key);
    localStorage.removeItem(ACTIVE_ORG_KEY);
    queryClient.clear();
    setState({
      user: null,
      organization: null,
      organizations: [],
      role: null,
      permissions: [],
      entitlements: null,
      isAuthenticated: false,
      isInitialized: true,
      isFetched: false,
      accessFetched: false,
    });
  }, [queryClient]);

  // Stable reference so consumers' useEffect([..., methods]) don't re-run on every Provider re-render
  const methods = useMemo(
    () => ({
      setUser,
      setOrganization,
      setOrganizations,
      setRole,
      setPermissions,
      setEntitlements,
      setIsAuthenticated,
      setIsInitialized,
      setIsFetched,
      setAccessFetched,
      setTokens,
      logout,
    }),
    [
      setUser,
      setOrganization,
      setOrganizations,
      setRole,
      setPermissions,
      setEntitlements,
      setIsAuthenticated,
      setIsInitialized,
      setIsFetched,
      setAccessFetched,
      setTokens,
      logout,
    ]
  );

  const value: Types.IContext.Value = useMemo(
    () => ({ state, methods }),
    [state, methods]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
